import React from 'react';
import { getComplementaryColors, getCelebrityExamples, getTraditionalOutfitRecommendations } from '../utils/styleRecommendations';

const RecommendationCard = ({ title, items = [], icon, color = "primary" }) => {
  return (
    <div className={`bg-white p-4 rounded-lg shadow-md border-t-4 border-${color}-500 hover:shadow-lg transition duration-300`}>
      <div className="flex items-center mb-3 justify-between">
        <div className="flex items-center">
          <div className={`p-2 rounded-full bg-${color}-100 mr-3`}>
            {icon}
          </div>
          <h3 className="text-lg font-semibold">{title}</h3>
        </div>
      </div>
      
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-start bg-gray-50 p-2 rounded-md">
            <svg className={`h-5 w-5 text-${color}-600 mt-0.5 mr-2 flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-gray-700">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ColorPalette = ({ colors = [] }) => {
  return (
    <div className="flex flex-wrap mt-3 gap-2">
      {colors.map((color, index) => (
        <div key={index} className="flex flex-col items-center">
          <div 
            className="w-10 h-10 rounded-full shadow-sm border border-gray-200" 
            style={{ backgroundColor: color }}
          ></div>
          <span className="text-xs text-gray-500 mt-1">{color}</span>
        </div>
      ))}
    </div>
  );
};

const CelebrityExamples = ({ celebrities = [] }) => {
  if (celebrities.length === 0) return null;
  
  return (
    <div className="bg-white p-4 rounded-lg shadow-md border-t-4 border-purple-500">
      <div className="flex items-center mb-3">
        <div className="p-2 rounded-full bg-purple-100 mr-3">
          <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold">Celebrity Examples</h3>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
        {celebrities.map((celebrity, index) => (
          <div key={index} className="text-center p-2 bg-gray-50 rounded-md">
            <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-purple-700 font-bold text-xl">{celebrity.charAt(0)}</span>
            </div>
            <p className="text-sm">{celebrity}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const TraditionalOutfits = ({ traditional }) => {
  if (!traditional || !traditional.indian || !traditional.western) return null;
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
      <div className="bg-white p-4 rounded-lg shadow-md border-t-4 border-indigo-500">
        <div className="flex items-center mb-3">
          <div className="p-2 rounded-full bg-indigo-100 mr-3">
            <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold">Indian Traditional Styles</h3>
        </div>
        
        <ul className="space-y-2">
          {traditional.indian.map((item, index) => (
            <li key={index} className="flex items-start bg-gray-50 p-2 rounded-md">
              <svg className="h-5 w-5 text-indigo-600 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gray-700">{item}</span>
            </li>
          ))}
        </ul>
      </div>
      
      <div className="bg-white p-4 rounded-lg shadow-md border-t-4 border-teal-500">
        <div className="flex items-center mb-3">
          <div className="p-2 rounded-full bg-teal-100 mr-3">
            <svg className="h-6 w-6 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold">Western Traditional Styles</h3>
        </div>
        
        <ul className="space-y-2">
          {traditional.western.map((item, index) => (
            <li key={index} className="flex items-start bg-gray-50 p-2 rounded-md">
              <svg className="h-5 w-5 text-teal-600 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-gray-700">{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const ColorRecommendations = ({ colorRecs }) => {
  if (!colorRecs || !colorRecs.best || !colorRecs.avoid) return null;
  
  return (
    <div className="bg-white p-4 rounded-lg shadow-md border-t-4 border-green-500 mt-6">
      <div className="flex items-center mb-3">
        <div className="p-2 rounded-full bg-green-100 mr-3">
          <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold">Color Recommendations</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium mb-2 text-green-800">Colors That Work Well</h4>
          <ul className="space-y-2">
            {colorRecs.best.map((item, index) => (
              <li key={index} className="flex items-start bg-gray-50 p-2 rounded-md">
                <svg className="h-5 w-5 text-green-600 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        
        <div>
          <h4 className="font-medium mb-2 text-red-800">Colors To Use Cautiously</h4>
          <ul className="space-y-2">
            {colorRecs.avoid.map((item, index) => (
              <li key={index} className="flex items-start bg-gray-50 p-2 rounded-md">
                <svg className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      
      {colorRecs.colorPalette && (
        <div className="mt-4">
          <h4 className="font-medium mb-2 text-gray-700">Recommended Color Palette</h4>
          <ColorPalette colors={colorRecs.colorPalette} />
        </div>
      )}
    </div>
  );
};

// Default SVG paths for when icons are not available
const defaultIcons = {
  glasses: "M15 8a2 2 0 1 0-4 0m4 0a2 2 0 1 1-4 0m4 0h6m-12 0h6m4 4H3m14-4a4 4 0 0 1 0 8",
  hairStyles: "M18 20a6 6 0 0 0-12 0m12-11A3 3 0 0 0 6 9c0 1.038 1.277 1.928 2 2.5C4.5 13 4 15.893 4 17.5a6.5 6.5 0 0 0 13 0c0-1.607-.5-4.5-4-6 .723-.572 2-1.462 2-2.5a3 3 0 0 0-3-3",
  clothingColors: "M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zm12 7c0 1.67 1.34 3 3 3h6V9h-6c-1.66 0-3 1.34-3 3"
};

const RecommendationsPanel = ({ faceShape, recommendations }) => {
  if (!faceShape || !recommendations) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-md text-center">
        <p className="text-gray-500">No recommendations available yet.</p>
      </div>
    );
  }
  
  // Safely extract properties with fallbacks
  const glasses = recommendations.glasses || [];
  const hairStyles = recommendations.hairStyles || [];
  const clothingColors = recommendations.clothingColors || [];
  const icons = recommendations.icons || defaultIcons;
  
  // Safely get data from utilities with error handling
  let colorRecommendations;
  let celebrities;
  let traditionalOutfits;
  
  try {
    colorRecommendations = getComplementaryColors(faceShape);
    celebrities = getCelebrityExamples(faceShape);
    traditionalOutfits = getTraditionalOutfitRecommendations(faceShape);
  } catch (error) {
    console.error("Error fetching recommendations:", error);
    // Set defaults if there's an error
    colorRecommendations = { best: [], avoid: [], colorPalette: [] };
    celebrities = [];
    traditionalOutfits = { indian: [], western: [] };
  }
  
  // Create SVG icons using the paths from the recommendations
  const createSvgIcon = (pathData, color) => (
    <svg className={`h-6 w-6 text-${color}-600`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={pathData} />
    </svg>
  );
  
  return (
    <div className="space-y-6">
      <div className="text-center bg-white p-6 rounded-lg shadow-md mb-6">
        <h2 className="text-2xl font-bold">Style Recommendations</h2>
        <p className="text-gray-600 mt-2">
          Based on your <span className={`font-semibold text-faceShapes-${faceShape}`}>{faceShape}</span> face shape, here are personalized style recommendations to enhance your natural features
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <RecommendationCard 
          title="Glasses Styles"
          items={glasses}
          color="blue"
          icon={createSvgIcon(icons.glasses || defaultIcons.glasses, "blue")}
        />
        
        <RecommendationCard 
          title="Hairstyles"
          items={hairStyles}
          color="amber"
          icon={createSvgIcon(icons.hairStyles || defaultIcons.hairStyles, "amber")}
        />
        
        <RecommendationCard 
          title="Clothing Styles"
          items={clothingColors}
          color="pink"
          icon={createSvgIcon(icons.clothingColors || defaultIcons.clothingColors, "pink")}
        />
      </div>
      
      <ColorRecommendations colorRecs={colorRecommendations} />
      
      <CelebrityExamples celebrities={celebrities} />
      
      <TraditionalOutfits traditional={traditionalOutfits} />
      
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md mt-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              These recommendations are guidelines to complement your facial features. Feel free to experiment with different styles to find what works best for you!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecommendationsPanel;